<template>
  <div class="grid min-w-0 grid-cols-2 gap-1 sm:grid-cols-3" role="list">
    <div
      v-for="row in rows"
      :key="row.kind"
      class="min-w-0 rounded-sm border border-border bg-bg-surface p-1"
      role="listitem"
    >
      <span class="block truncate text-2xs font-semibold uppercase tracking-wide text-text-muted">{{ row.label }}</span>
      <span v-if="showWeaponNames" class="mt-1 flex min-w-0 flex-col gap-1">
        <span
          v-for="(slug, index) in row.weaponSlugs"
          :key="`${row.kind}-named-${index}`"
          class="flex min-w-0 items-center gap-1"
          :title="weaponName(slug)"
        >
          <img
            v-if="weaponIcon(slug)"
            :src="weaponIcon(slug)"
            alt=""
            class="h-6 w-6 shrink-0 rounded-sm object-cover"
            draggable="false"
          >
          <span
            v-else
            class="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-sm border border-dashed border-border-strong text-xs text-text-muted"
            aria-hidden="true"
          >+</span>
          <span class="min-w-0 truncate text-2xs text-text-secondary">{{ weaponName(slug) }}</span>
        </span>
      </span>
      <span v-else class="mt-0.5 flex items-center gap-0.5">
        <template v-for="(slug, index) in row.weaponSlugs" :key="`${row.kind}-${index}`">
          <img
            v-if="weaponIcon(slug)"
            :src="weaponIcon(slug)"
            :alt="weaponName(slug)"
            class="h-6 w-6 shrink-0 rounded-sm object-cover"
            draggable="false"
          >
          <span
            v-else
            class="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-sm border border-dashed border-border-strong text-xs text-text-muted"
            aria-hidden="true"
          >+</span>
        </template>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { WEAPONS_BY_SLUG } from '@/data/weapons'
import { weaponDisplayName } from '../../lib/roleLabels'
import { presetStage } from '../../lib/buildPresets'
import { BUILD_PRESET_STAGE_KINDS } from '../../types'
import type { BuildPreset, RaidStageKind } from '../../types'
import { useBuildPresetMessages } from './locales'

export interface BuildPresetPreviewProps {
  readonly preset: BuildPreset
  readonly showWeaponNames?: boolean
}

interface PreviewRow {
  readonly kind: RaidStageKind
  readonly label: string
  readonly weaponSlugs: ReadonlyArray<string>
}

const props = withDefaults(defineProps<BuildPresetPreviewProps>(), {
  showWeaponNames: false,
})
const messages = useBuildPresetMessages()

const rows = computed<ReadonlyArray<PreviewRow>>(() =>
  BUILD_PRESET_STAGE_KINDS.map((kind) => ({
    kind,
    label: messages.value.stages[kind],
    weaponSlugs: presetStage(props.preset, kind)?.weaponSlugs ?? [],
  })),
)

function weaponIcon(slug: string): string | undefined {
  return WEAPONS_BY_SLUG.get(slug)?.icon
}

function weaponName(slug: string): string {
  const weapon = WEAPONS_BY_SLUG.get(slug)
  return weapon === undefined
    ? slug
    : weaponDisplayName(slug, weapon.name, messages.value.names)
}
</script>
